import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError, MatterExpense } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  EXPENSE_PAYEE_CONNECTION_TYPE,
  getExpensePayeeConnection,
} from "@/lib/utils/connections/expense-contact-connections";
import { syncSingleContactConnection } from "@/lib/utils/connections/item-contact-connections";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const useSyncExpensePayeeConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      matterId,
      currentExpense,
      desiredContactId,
      expenseId,
    }: {
      currentExpense?: MatterExpense;
      desiredContactId?: string;
      expenseId: string;
      matterId: string;
    }) =>
      syncSingleContactConnection({
        matterId,
        currentConnections: currentExpense?.connections,
        connectionType: EXPENSE_PAYEE_CONNECTION_TYPE,
        current: getExpensePayeeConnection(currentExpense),
        desiredContactId,
        sourceObjectId: expenseId,
        sourceObjectType: "matter_expense",
      }),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating expense payee");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
  });
};
